import React, { useState, useRef, useMemo } from 'react';
import {
  Grid,
  TextField,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { supplierService } from '../../utils/apiService';
import { debounce } from '../../utils/invoiceCalculations';

/**
 * Normalizes supplier objects to a standardized shape.
 * 
 * @param {Object} supplier - Raw supplier data object
 * @returns {Object|null} 
 */
const normalizeSupplier = (supplier) => {
  if (!supplier) return null;
  return {
    id: supplier.id ?? supplier._id ?? '',
    name: supplier.name ?? 'Unknown Supplier',
    vatNumber: supplier.vatRegistrationNumber ?? supplier.vatNumber ?? 'N/A',
    email: supplier.email ?? 'N/A'
  };
};

/**
 * Fetches suppliers from the API and returns them as normalized supplier objects.
 * 
 * @param {string} query - Search query
 * @returns {Promise<Array>} List of normalized supplier objects
 */
const fetchSuppliers = async (query) => {
  const response = await supplierService.getSuppliers(1, 10, query);
  const suppliersList =
    (Array.isArray(response) && response) ||
    (Array.isArray(response?.suppliers) && response.suppliers) ||
    (Array.isArray(response?.data) && response.data) ||
    (Array.isArray(response?.docs) && response.docs) ||
    [];

  return suppliersList.map(normalizeSupplier);
};

/**
 * SupplierInformation component
 * Displays and manages supplier information for purchase invoices
 * 
 * @param {Object} props
 * @param {Object} props.invoice - The invoice data object containing supplier information
 * @param {Array} props.suppliers - List of available suppliers
 * @param {Function} props.handleSupplierChange - Function to handle supplier selection changes
 */
const SupplierInformation = ({
  invoice,
  suppliers,
  handleSupplierChange
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const debouncedSupplierSearch = useRef(
    debounce(async (query) => {
      try {
        const results = await fetchSuppliers(query);
        setSearchResults(results);
        setSearchError(null);
      } catch (err) {
        console.error('Error searching suppliers:', err);
        setSearchError(err.message ?? 'Error fetching suppliers');
      } finally {
        setSearchLoading(false);
      }
    }, 300)
  ).current;

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    debouncedSupplierSearch(query);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input') {
      handleSearchChange(newInputValue);
    } else if (reason === 'clear' || !newInputValue) {
      handleSearchChange('');
    } else if (reason === 'reset') {
      setSearchQuery('');
      setSearchResults([]);
      setSearchError(null);
    }
  };

  const autocompleteOptions = useMemo(() => {
    const baseList = searchQuery ? searchResults : (suppliers ?? []);
    let filteredList = baseList.map(normalizeSupplier);

    const walkInSupplier = { id: 0, name: 'Walk In Supplier', vatNumber: 'N/A', email: 'N/A' };
    const hasWalkIn = filteredList.some(
      (s) => s.id === 0 || s.name?.toLowerCase().includes('walk in')
    );

    if (!hasWalkIn) {
      const lowerQuery = searchQuery.toLowerCase();
      if (
        !searchQuery ||
        'walk in'.includes(lowerQuery) ||
        'walk in supplier'.includes(lowerQuery)
      ) {
        filteredList = [walkInSupplier, ...filteredList];
      }
    }

    // Ensure currently selected supplier is in the list
    if (invoice?.supplier) {
      const normalizedSelected = normalizeSupplier(invoice.supplier);
      const alreadyExists = filteredList.some((opt) => opt.id === normalizedSelected.id);
      if (!alreadyExists) {
        filteredList = [normalizedSelected, ...filteredList];
      }
    }

    return filteredList;
  }, [searchQuery, searchResults, suppliers, invoice?.supplier]);

  return (
    <Grid container spacing={0.5}>
      <Grid item xs={12}>
        <Autocomplete
          options={autocompleteOptions}
          getOptionLabel={(option) => option?.name ?? ''}
          isOptionEqualToValue={(option, value) => {
            const optionId = option?.id ?? option?._id;
            const valueId = value?.id ?? value?._id;
            return optionId !== undefined && valueId !== undefined && optionId === valueId;
          }}
          value={normalizeSupplier(invoice?.supplier)}
          onChange={handleSupplierChange}
          onInputChange={handleInputChange}
          filterOptions={(x) => x}
          loading={searchLoading}
          loadingText="Searching..."
          noOptionsText={searchError ? `Error: ${searchError}` : "No suppliers found"}
          renderOption={(props, option) => (
            <li {...props} key={option.id ?? option._id}>
              {option.name ?? 'Unknown Supplier'}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Supplier"
              margin="dense"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  height: 36,
                },
                '& .MuiInputBase-input': {
                  py: 0.75,
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                },
                '& .MuiSelect-select': {
                  py: 0.75
                }
              }}
            />
          )}
        />
      </Grid>
      {invoice?.supplier && (invoice.supplier.id ?? invoice.supplier._id) !== 0 && (
        <>
          <Grid item xs={12}>
            <TextField
              label="VAT Number"
              value={invoice.supplier.vatNumber ?? invoice.supplier.vatRegistrationNumber ?? 'N/A'}
              fullWidth
              margin="dense"
              size="small"
              InputProps={{
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  height: 36,
                },
                '& .MuiInputBase-input': {
                  py: 0.75,
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                },
                '& .MuiSelect-select': {
                  py: 0.75
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              value={invoice.supplier.email ?? 'N/A'}
              fullWidth
              margin="dense"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  height: 36,
                },
                '& .MuiInputBase-input': {
                  py: 0.75,
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                },
                '& .MuiSelect-select': {
                  py: 0.75
                }
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default SupplierInformation;
